import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    CartProductType,
    MY_CART_DETAILS,
    StartNegotiationResponse,
    checkListingAlreadyInCart,
    handleCartError,
    updateCartCache,
    useAddItemToCart,
    useCartDetails,
    useFeatureFlag,
    useUserSelfDetails,
    getAvailableCredit,
    useSelfUpdates,
    MY_OFFERS_LISTING_KEY,
    useFetchDynamicDeliveryPrice,
} from '@steelbuy/data-access';
import { FetchStatus, useListingBuyerEntityData } from '@steelbuy/data-provider';
import { NotFoundError } from '@steelbuy/error';
import { useModal } from '@steelbuy/modal-dialog';
import {
    CartBuyerModel,
    Feature,
    getMinimumTargetPrice,
    getPricingUnit,
    getTradeUnitCalculation,
    ListingStatus,
    OfferListingStatus,
    OfferView,
    TradeUnitCalculation,
    UserRole,
    createDateFromTimestamp,
    getTradeUnit,
    DeliveryPromise,
} from '@steelbuy/ts-shared';

import {
    ListingBuyerDetailsPrice,
    ListingDetailsExpiration,
    MarketingBannerSteelbuyPromise,
    MakeAnOfferModal,
} from '@steelbuy/ui-domain';
import {
    ButtonCallToAction,
    ButtonSecondary,
    ButtonTertiaryOnLightM,
    Card,
    CardContentAttributes,
    CardContentAttributesItem,
    CardContentMaterialLocation,
    CardLayout,
    LoadingStatus,
    Notification,
    NotificationLevel,
    toaster,
    useSuccessErrorNotification,
} from '@steelbuy/ui-primitive';
import { useIsVendorFeatureSupported, useVendor } from '@steelbuy/vendor';

import { ListingDetailsDelivery } from './ListingDetailsDelivery';
import { OfferInProgressModal } from './OfferInProgressModal';
import { DetailsLayout } from '../../views/layouts/details-layout/DetailsLayout';
import { AddToCartConfirmModal } from '../add-to-cart-confirm-modal/AddToCartConfirmModal';
import { useCheckoutContext } from '../checkout/CheckoutContext';
import { CommonListingDetails } from '../common-listing-details/CommonListingDetails';
import { MaterialPageHeader } from '../material-page-header/MaterialPageHeader';
import { Negotiations, hasActiveOffer } from '../offers/Negotiations';

import './ListingDetails.scss';

interface ListingDetailsProps {
    checkoutPath: string;
    goBackPath: string;
    offerSuccessPath: string;
}

const DELIVERY_COST_ERROR_CODE = 3001;

export const ListingDetails = ({ checkoutPath, goBackPath, offerSuccessPath }: ListingDetailsProps) => {
    const { t } = useTranslation(['translation', 'domainModel', 'uiDomain', 'uiPrimitive']);

    const {
        Notification: UpdateNotification,
        showSuccessNotification,
        showErrorNotification,
    } = useSuccessErrorNotification();

    const openOfferModal = useModal();
    const openAddToCartModal = useModal();
    const offerInProgressModal = useModal();
    const { isFeatureSupported } = useFeatureFlag();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { data: userData } = useUserSelfDetails();
    const checkoutContext = useCheckoutContext();
    const listingEntityData = useListingBuyerEntityData();
    const listingModel = listingEntityData
        .query()
        .getOrThrow(new NotFoundError(t('translation:application.listingNotFound.notFoundMessage')));

    const hasBuyerRole = userData?.roles.includes(UserRole.BUYER) ?? false;
    const addItemToCartMutation = useAddItemToCart();
    const { refetch, data } = useCartDetails({ queryOptions: { enabled: false } });
    const { refetch: refetchCache } = useSelfUpdates();
    const {
        data: listingDetailsData,
        isFetching: fetchingDeliveryPrice,
        refetch: getDynamicDeliveryPrice,
    } = useFetchDynamicDeliveryPrice({
        listingId: listingModel.id,
        queryOptions: {
            enabled: false,
            onSuccess: (listingWithDynamicDelivery) => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                listingEntityData.updateCache(listingModel.id, listingWithDynamicDelivery as any);
            },
        },
    });

    const [cartData, setCartData] = useState<CartBuyerModel>();
    const [buttonClickedType, setButtonClickedType] = useState<'CART' | 'CHECKOUT'>();

    const negotiationExists = listingModel?.negotiationExists;
    const isDynamicDeliveryPriceEnabled = useIsVendorFeatureSupported('DYNAMIC_DELIVERY_PRICE');

    const priceFreightFetchError =
        isDynamicDeliveryPriceEnabled &&
        listingDetailsData?.pricing?.deliveryCostError?.code === DELIVERY_COST_ERROR_CODE;

    const priceFreightUnavailable =
        isDynamicDeliveryPriceEnabled &&
        !listingModel?.pricing?.priceIncludingEstimatedDelivery?.value &&
        !listingDetailsData?.pricing?.priceIncludingEstimatedDelivery?.value;

    const priceFetchError = !fetchingDeliveryPrice && (priceFreightUnavailable || priceFreightFetchError);

    listingEntityData.useFetchStatusEffect([FetchStatus.SUCCESS], () => {
        if (priceFreightUnavailable) {
            getDynamicDeliveryPrice();
        }
    });

    const isNegotiating =
        listingModel?.negotiations?.some(
            (negotiation) =>
                negotiation.status === OfferListingStatus.AWAITING_BUYER ||
                negotiation.status === OfferListingStatus.AWAITING_SELLER
        ) ?? false;

    const doCheckout = () => {
        checkoutContext.initializeWithListingId(listingModel.id);
        if (pathname) {
            navigate(checkoutPath, { state: pathname });
        } else {
            navigate(checkoutPath);
        }
    };

    const showFreightQuoteToast = () => {
        if (fetchingDeliveryPrice) {
            toaster.info(t('translation:application.listingDetails.fetchingPriceMessage'));
            return true;
        }
        if (priceFetchError) {
            toaster.error(t('uiPrimitive:notification.freightQuoteUnavailable'));
            return true;
        }
        return false;
    };

    const handleCheckoutButtonClick = () => {
        if (showFreightQuoteToast()) {
            return;
        }
        if (negotiationExists) {
            setButtonClickedType('CHECKOUT');
            offerInProgressModal.show();
        } else {
            doCheckout();
        }
    };

    const queryClient = useQueryClient();

    const addToCart = () => {
        addItemToCartMutation.mutate(
            { productId: listingModel?.id, productType: CartProductType.LISTING },
            {
                onSuccess: (response) => {
                    setCartData(response);
                    offerInProgressModal.hide();
                    openAddToCartModal.show();
                    updateCartCache(queryClient, [MY_CART_DETAILS], response);
                },
                onError: (e: unknown) => {
                    handleCartError(e, refetch, t);
                },
            }
        );
    };

    listingEntityData.useFetchStatusEffect([FetchStatus.SUCCESS], () => {
        refetchCache();
    });

    const handleAddToCart = (): void => {
        if (showFreightQuoteToast()) {
            return;
        }
        if (negotiationExists) {
            setButtonClickedType('CART');
            offerInProgressModal.show();
        } else {
            addToCart();
        }
    };

    const refetchListingDetails = (
        notificationMessage?: string,
        errorNotificationMessage?: string,
        forceRefetch?: boolean
    ) => {
        if (notificationMessage) {
            showSuccessNotification(notificationMessage);
        } else if (errorNotificationMessage) {
            showErrorNotification(errorNotificationMessage);
        }
        if (!errorNotificationMessage || forceRefetch) {
            listingEntityData.resolveFetchStatus();
        }
    };

    const pricingUnit = getPricingUnit(listingModel.material);
    const tradeUnit = getTradeUnit(listingModel.material);
    const tradeUnitCalculation = getTradeUnitCalculation(listingModel.material);
    const weightOrItems =
        tradeUnitCalculation === TradeUnitCalculation.BY_WEIGHT ? listingModel.weight : listingModel.numberOfItems || 0;

    const isBidOfferEnabled =
        isFeatureSupported(Feature.BID_OFFER) &&
        !negotiationExists &&
        !listingModel?.negotiations?.some((negotiation) => negotiation.status === OfferListingStatus.ACCEPTED);

    const isCartSupported = isFeatureSupported(Feature.CART) && hasBuyerRole;

    const taxPercent = listingModel.pricing.taxPercent / 100;

    const listingInCart = data && checkListingAlreadyInCart(data, listingModel.id);

    const showAge = useVendor() !== 'TNA';
    const showDeliveryPromise =
        useIsVendorFeatureSupported('DELIVERY_PROMISE') &&
        listingModel?.deliveryPromise !== DeliveryPromise.UNSPECIFIED;

    const renderActionButtons = () => {
        if (!hasBuyerRole) {
            return null;
        }
        return (
            <div
                className={classNames('listing-details__action-bar', {
                    'listing-details__action-bar-with-offer': isBidOfferEnabled,
                })}
            >
                {isBidOfferEnabled && (
                    <ButtonTertiaryOnLightM
                        label={t('translation:application.listingDetails.offerButtonText')}
                        onClick={openOfferModal.show}
                        disabled={hasActiveOffer(listingModel?.negotiations) || addItemToCartMutation.isLoading}
                    />
                )}
                {isCartSupported && (
                    <ButtonSecondary
                        label={
                            listingInCart ? t('uiDomain:commonList.addedToCart') : t(`uiDomain:commonList.addToCart`)
                        }
                        onClick={handleAddToCart}
                        disabled={listingInCart}
                        loadingStatus={addItemToCartMutation.isLoading ? LoadingStatus.PENDING : LoadingStatus.IDLE}
                    />
                )}
                <ButtonCallToAction
                    label={t('translation:application.listingDetails.checkoutButtonText')}
                    onClick={handleCheckoutButtonClick}
                    disabled={!listingModel.canCheckout || addItemToCartMutation.isLoading}
                />
            </div>
        );
    };

    return (
        <>
            <MaterialPageHeader
                material={listingModel.material}
                previousPageTitle={t('uiDomain:common.back')}
                onBackClick={() => navigate(goBackPath)}
            />

            <div className="listing-details">
                <DetailsLayout>
                    {!isNegotiating && negotiationExists && (
                        <Notification
                            level={NotificationLevel.INFO}
                            message={t('translation:application.checkout.negotiationExists')}
                        />
                    )}

                    {listingModel.listingStatusType !== ListingStatus.PURCHASED && renderActionButtons()}
                    {hasBuyerRole && !listingModel.canCheckout && (
                        <Notification
                            level={NotificationLevel.ERROR}
                            message={t('translation:application.checkout.creditLimitExceeded', {
                                email: t('translation:constants.emailSupport'),
                            })}
                        />
                    )}

                    {UpdateNotification}
                    <Negotiations
                        negotiations={listingModel.negotiations}
                        view={OfferView.BUYER}
                        onStatusChange={refetchListingDetails}
                        pricingUnit={pricingUnit}
                        weightOrItems={weightOrItems}
                        taxPercent={taxPercent}
                        numberOffers={listingModel.totalNumberOfNegotiation}
                        numberActiveOffers={listingModel.numberOfActiveNegotiation}
                        minimumPrice={getMinimumTargetPrice(
                            listingModel.material,
                            listingModel.weight,
                            listingModel.numberOfItems
                        )}
                        listing={listingModel}
                    />
                    <CommonListingDetails listingModel={listingModel} showRequestCertificateCta showAge={showAge} />
                    <ListingBuyerDetailsPrice
                        listing={listingDetailsData ?? listingModel}
                        priceFetchError={priceFetchError}
                        isPriceFetching={fetchingDeliveryPrice}
                    />
                    {/* Delivery CARD */}
                    <Card isClickable={false}>
                        <CardLayout>
                            <CardContentAttributes header={t('translation:application.listingDetails.deliveryTitle')}>
                                {showDeliveryPromise && (
                                    <CardContentAttributesItem
                                        label={t('translation:application.listingDetails.deliveryText')}
                                        value={t(`domainModel:deliveryPromise.${listingModel.deliveryPromise}`)}
                                    />
                                )}
                                {isDynamicDeliveryPriceEnabled ? (
                                    <ListingDetailsDelivery listingModel={listingModel} />
                                ) : (
                                    listingModel.country && (
                                        <CardContentMaterialLocation
                                            countryCode={listingModel.country}
                                            state={listingModel.state}
                                        />
                                    )
                                )}
                            </CardContentAttributes>
                        </CardLayout>
                    </Card>
                    <ListingDetailsExpiration expiryDate={createDateFromTimestamp(listingModel.expiresAt)} />
                    <MarketingBannerSteelbuyPromise />
                    {listingModel.listingStatusType !== ListingStatus.PURCHASED && renderActionButtons()}
                    <MakeAnOfferModal
                        modalAccessor={openOfferModal}
                        currencyCode={listingModel.pricing.currencyCode}
                        creditLimit={getAvailableCredit(userData || null)}
                        originalPrice={listingModel.pricing.priceIncludingDelivery.value}
                        pricingUnit={pricingUnit}
                        weightOrItems={weightOrItems}
                        listingId={listingModel.id}
                        onSuccess={({ referenceId }: StartNegotiationResponse) => {
                            listingEntityData.resolveFetchStatus();
                            queryClient.removeQueries({ queryKey: [MY_OFFERS_LISTING_KEY] });
                            navigate(offerSuccessPath, {
                                state: {
                                    referenceId,
                                },
                            });
                        }}
                        taxPercent={taxPercent}
                        minimumPrice={getMinimumTargetPrice(
                            listingModel.material,
                            listingModel.weight,
                            listingModel.numberOfItems
                        )}
                        tradeUnit={tradeUnit}
                        tradeUnitCalculation={tradeUnitCalculation}
                        listing={listingModel}
                    />
                    {cartData && <AddToCartConfirmModal modalAccessor={openAddToCartModal} cartData={cartData} />}

                    <OfferInProgressModal
                        offerInProgressModal={offerInProgressModal}
                        header={
                            buttonClickedType === 'CART'
                                ? t('application.offers.addToCartModalHeader')
                                : t('application.offers.buyNowModalheader')
                        }
                        handleBuyNow={buttonClickedType === 'CART' ? addToCart : doCheckout}
                        buttonLabel={
                            buttonClickedType === 'CART'
                                ? t('uiDomain:commonList.addToCart')
                                : t('application.offers.buynow')
                        }
                    />
                </DetailsLayout>
            </div>
        </>
    );
};
